/*
  AdvancedSection.jsx
*/

// Npm.
import React, { useState } from 'react';

// Components.
import BottomActions from './BottomActions';
import Button from '../../Button';
import CheckboxWithLabel from '../../CheckboxWithLabel';

// Hooks.
import useThrottle from '../../../hooks/useThrottle';

/**
 * Convert a string to a lowercase slug format.
 * e.g., "foo  bar Baz" -> "foo-bar-baz"
 */
function slugify(text) {
  if (!text) return text;
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')  // Replace whitespace with dashes
    .replace(/-+/g, '-');  // Replace multiple dashes with single dash
}

/**
 * Section for advanced graph data operations.
 */
export default function AdvancedSection({
  className,
  graphData,
  onChangeGraphData,
  ...rest
}) {

  const [overwriteEdgeLabels, setOverwriteEdgeLabels] = useState(false);

  // Format node labels to lowercase slugs.
  const handleFormatNodeLabels = useThrottle(() => {
    if (onChangeGraphData) {
      onChangeGraphData(data => {

        // Build a map of updated nodes so we can also refresh link references.
        const nodeMap = new Map();
        const nodes = data.nodes.map(node => {
          const nextNode = {
            ...node,
            _botech: {
              ...node._botech,
              label: slugify(node._botech?.label),
            },
          };
          if (node.id) {
            nodeMap.set(node.id, nextNode);
          }
          return nextNode;
        });

        // Make sure links continue to point at the latest node objects.
        const remapNode = (nodeRef) => {
          if (!nodeRef) return nodeRef;
          const id = typeof nodeRef === 'object' ? nodeRef.id : nodeRef;
          return (id && nodeMap.get(id)) || nodeRef;
        };

        const links = data.links.map(link => {
          const nextSource = remapNode(link.source);
          const nextTarget = remapNode(link.target);

          if (nextSource !== link.source || nextTarget !== link.target) {
            return { ...link, source: nextSource, target: nextTarget };
          }

          return link;
        });

        return { ...data, nodes, links };
      });
    }
  }, 500);

  // Autopopulate edge labels from source and target node labels.
  const handleAutopopulateEdgeLabels = useThrottle(() => {
    if (onChangeGraphData) {
      onChangeGraphData(data => {

        const nodeMap = new Map(data.nodes.map(node => [node.id, node]));

        const getNodeFromMap = (nodeRef) => {
          if (!nodeRef) return nodeRef;
          const id = typeof nodeRef === 'object' ? nodeRef.id : nodeRef;
          return (id && nodeMap.get(id)) || nodeRef;
        };

        const getNodeLabel = (nodeRef) => {
          const node = getNodeFromMap(nodeRef);
          if (typeof node === 'object') {
            return node._botech?.label || node.id || 'untitled';
          }
          return node || 'untitled';
        };

        const links = data.links.map(link => {
          const sourceNode = getNodeFromMap(link.source);
          const targetNode = getNodeFromMap(link.target);
          const newLabel = `${getNodeLabel(sourceNode)} -> ${getNodeLabel(targetNode)}`;

          // Only update if overwrite is enabled or label is empty.
          const shouldUpdateLabel = overwriteEdgeLabels || !link._botech?.label;

          if (
            shouldUpdateLabel
            || sourceNode !== link.source
            || targetNode !== link.target
          ) {
            return {
              ...link,
              source: sourceNode,
              target: targetNode,
              _botech: shouldUpdateLabel
                ? { ...link._botech, label: newLabel }
                : link._botech,
            };
          }

          return link;
        });

        return { ...data, links };
      });
    }
  }, 500);

  // Format subroutines by removing non-existent node/edge IDs.
  const handleFormatSubroutines = useThrottle(() => {
    if (onChangeGraphData) {
      onChangeGraphData(data => {
        const subroutines = data._botech?.subroutines;

        if (!subroutines || subroutines.length === 0) {
          return data;
        }

        // Create sets of valid IDs for quick lookup
        const validNodeIds = new Set(data.nodes.map(node => node.id));
        const validLinkIds = new Set(data.links.map(link => link.id).filter(Boolean));

        // Filter out non-existent IDs from each subroutine
        const cleanedSubroutines = subroutines.map(subroutine => {
          const cleaned = { ...subroutine };

          // Clean node ID arrays
          if (cleaned.included_source_nodes) {
            cleaned.included_source_nodes = cleaned.included_source_nodes.filter(id => validNodeIds.has(id));
          }
          if (cleaned.excluded_source_nodes) {
            cleaned.excluded_source_nodes = cleaned.excluded_source_nodes.filter(id => validNodeIds.has(id));
          }
          if (cleaned.included_target_nodes) {
            cleaned.included_target_nodes = cleaned.included_target_nodes.filter(id => validNodeIds.has(id));
          }
          if (cleaned.excluded_target_nodes) {
            cleaned.excluded_target_nodes = cleaned.excluded_target_nodes.filter(id => validNodeIds.has(id));
          }

          // Clean edge ID arrays
          if (cleaned.included_edges) {
            cleaned.included_edges = cleaned.included_edges.filter(id => validLinkIds.has(id));
          }
          if (cleaned.excluded_edges) {
            cleaned.excluded_edges = cleaned.excluded_edges.filter(id => validLinkIds.has(id));
          }

          return cleaned;
        });

        return {
          ...data,
          _botech: {
            ...data._botech,
            subroutines: cleanedSubroutines,
          },
        };
      });
    }
  }, 500);

  return (
    <div className={className}>

      <p className="body2 gutterBottom">Advanced operations for graph data:</p>

      <div style={{ marginTop: '16px' }}>

        <div style={{ marginBottom: '16px' }}>
          <p className="body2" style={{ marginBottom: '8px' }}>
            <strong>Format Node Labels</strong>
          </p>
          <p className="body2" style={{ marginBottom: '12px', color: '#888' }}>
            Converts all node labels to lowercase and replaces whitespace with dashes.
            <br />
            Example: "Foo  Bar Baz" → "foo-bar-baz"
          </p>
          <Button onClick={handleFormatNodeLabels}>
            Format Node Labels
          </Button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p className="body2" style={{ marginBottom: '8px' }}>
            <strong>Autopopulate Edge Labels</strong>
          </p>
          <p className="body2" style={{ marginBottom: '12px', color: '#888' }}>
            Creates edge labels in the format "source → target" based on connected nodes.
          </p>
          <CheckboxWithLabel
            label="Overwrite existing edge labels"
            checked={overwriteEdgeLabels}
            onChange={(e) => setOverwriteEdgeLabels(e.target.checked)}
            style={{ marginBottom: '12px' }}
          />
          <Button onClick={handleAutopopulateEdgeLabels}>
            Autopopulate Edge Labels
          </Button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p className="body2" style={{ marginBottom: '8px' }}>
            <strong>Format Subroutines</strong>
          </p>
          <p className="body2" style={{ marginBottom: '12px', color: '#888' }}>
            Removes non-existent node and edge IDs from all subroutines.
          </p>
          <Button onClick={handleFormatSubroutines}>
            Format Subroutines
          </Button>
        </div>

      </div>

    </div>
  );
}
